import cloneDeep from 'lodash/cloneDeep';
import uniqBy from 'lodash/uniqBy';
import i18n from 'i18n';
import { TFunction } from 'i18next';
import moment, { Moment } from 'moment-timezone';
import { campaignEditorDefaultState, calculateSendDateBounds } from '../state/campaignEditorReducer';
import {
  CampaignEditorState,
  PurchaseCheckUnit,
  TargetAudienceType,
  TargetLookbackPeriod,
} from '../state/CampaignEditorState';
import { SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { FilterOptionsFactory } from 'sections/offsite-campaigns/offsite-campaign-editor/components/editor/segment-item/segment-filter-items/FilterOptionsFactory';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';
import { OffsiteCampaignStatus, Segment, SegmentFilterOptions, SegmentFilterType, SubjectLine, Template } from 'models';
import { BulkRetargetingCampaign } from 'models/offsite/Campaign/BulkRetargetingCampaign';

export type CampaignStateIds = {
  targetAudience: string;
  segmentId: string;
  percentageSplitTestId?: string;
  cycleId?: string;
  purchaseCheckId?: string;
};

export function mapCampaignToState(
  campaign: BulkRetargetingCampaign,
  templates: Template[],
  subjectLines: SubjectLine[],
  t: TFunction,
): CampaignEditorState {
  const defaultState = cloneDeep(campaignEditorDefaultState);
  const state = {
    ...defaultState,
    campaign: {
      ...defaultState.campaign,
      name: campaign.name,
      bulkSortByFeature: campaign.bulkSortByFeature,
      sendDate: campaign.startTime ? new Date(campaign.startTime) : defaultState.campaign.sendDate,
    },
    status: mapCampaignStatus(campaign, defaultState),
    readOnly: campaign.state !== OffsiteCampaignStatus.Draft,
  };

  (campaign?.segments || []).forEach(segment => {
    const targetData = getTargetAudienceAndIdsFromSegment(segment);

    const key = targetData.key;

    if (!key) {
      return;
    }

    const purchaseData = getPurchaseCheckFromSegment(segment);
    const additionalData = getAdditionalSegmentationFromSegment(segment, t);
    const contentData = getContentFromSegment(segment, subjectLines, templates);

    state.campaign.targetAudience = uniqBy([...state.campaign.targetAudience, targetData.targetAudience], 'audience');
    state.campaign.additionalSegmentation[key] = additionalData.additionalSegmentation;
    // For new campaigns (no purchase filter), default to false. Only set to true if explicitly found in segment.
    state.campaign.enablePurchaseCheck[key] = purchaseData.enablePurchaseCheck ?? false;
    state.campaign.purchaseCheckDays[key] = purchaseData.purchaseCheckDays ?? 1;
    // Populate purchaseCheck object with data from backend for both old and new structures
    // Always populate it so the component can read the correct values when feature flag is enabled
    state.campaign.purchaseCheck = state.campaign.purchaseCheck || {};
    state.campaign.purchaseCheck[key] = {
      enablePurchaseCheck: purchaseData.enablePurchaseCheck ?? false,
      purchaseCheckDays: purchaseData.purchaseCheckDays ?? 1, // Default value, same pattern as target audience
      purchaseCheckMonths: purchaseData.purchaseCheckMonths ?? 1, // Default value
      purchaseCheckUnit: purchaseData.purchaseCheckUnit ?? PurchaseCheckUnit.Days,
      purchaseCheckStartDate: purchaseData.purchaseCheckStartDate ?? null,
      purchaseCheckEndDate: purchaseData.purchaseCheckEndDate ?? null,
      isPurchaseCheckUsingDateRange: purchaseData.isPurchaseCheckUsingDateRange ?? false,
    };
    state.campaign.ids.segmentId[key] = targetData.ids?.segmentId;
    state.campaign.ids.percentageSplitTestId[key] = targetData.ids?.percentageSplitTestId;
    state.campaign.ids.purchaseCheckId[key] = targetData.ids?.purchaseCheckId;
    state.campaign.ids.targetAudience[key] = targetData.ids?.targetAudience;
    state.campaign.ids.cycleId[key] = targetData.ids?.cycleId;
    state.campaign.subjectLine[key] = contentData.subjectLine;
    state.campaign.template[key] = contentData.template;
  });

  // Remove any additional segmentation for PurchaseCheck.
  const cleanedAdditionalSegmentation = { ...state.campaign.additionalSegmentation };
  delete cleanedAdditionalSegmentation[SegmentFilterOptions.PurchaseCheck];
  state.campaign.additionalSegmentation = cleanedAdditionalSegmentation;

  // Calculate maxSendDate based on targetAudiences
  const { minSendDate, maxSendDate } = calculateSendDateBounds(state.campaign.targetAudience);
  state.campaign.maxSendDate = maxSendDate;
  state.campaign.minSendDate = minSendDate;

  return state;
}

function mapCampaignStatus(
  response: BulkRetargetingCampaign,
  defaultState: CampaignEditorState,
): CampaignEditorState['status'] {
  if (!response.startTime && response.state === OffsiteCampaignStatus.Scheduled) {
    return OffsiteCampaignStatus.Triggered;
  }
  return (response.state as CampaignEditorState['status']) || defaultState.status;
}

function getTargetAudienceAndIdsFromSegment(
  segment: Segment<true>,
): {
  key?: TargetAudienceType;
  targetAudience?: {
    audience: TargetAudienceType;
    days: number;
    startDate?: Moment;
    endDate?: Moment;
    isUsingDateRange?: boolean;
  };
  ids?: CampaignStateIds;
} {
  const bulkFilters = getBulkRetargetingFilters(segment);
  const targetFilter = bulkFilters.find(filter => filter.options.find(filterByTargetAudience));
  if (targetFilter) {
    const key = targetFilter.options[0] as TargetAudienceType;
    let newTargetAudience: {
      audience: TargetAudienceType;
      days: number;
      dateType: TargetLookbackPeriod;
      startDate?: Moment;
      endDate?: Moment;
      isUsingDateRange?: boolean;
    };
    if (targetFilter.comparison === 'between') {
      const dates = String(targetFilter.value).split('|');
      // Parse UTC timestamps and convert to local time moments (preserving the calendar date)
      // Extract calendar date first to avoid timezone shift when converting end of day
      const startDateUTC = moment.utc(Number(dates[0]));
      const endDateUTC = moment.utc(Number(dates[1]));
      newTargetAudience = {
        audience: key,
        days: 1,
        dateType: TargetLookbackPeriod.Days,
        // Start date: convert UTC start of day to local start of day
        startDate: moment.tz(startDateUTC.format('YYYY-MM-DD') + 'T00:00:00', moment.tz.guess()),
        // End date: convert UTC calendar date to local end of day (preserve calendar date)
        endDate: moment.tz(endDateUTC.format('YYYY-MM-DD') + 'T23:59:59', moment.tz.guess()),
        isUsingDateRange: true,
      };
    } else {
      if (String(targetFilter.value).includes('M')) {
        newTargetAudience = {
          audience: key,
          days: Number(String(targetFilter.value).replace(/\D/g, '')), // remove non-numeric characters
          dateType: TargetLookbackPeriod.Months,
        };
      } else {
        newTargetAudience = {
          audience: key,
          days: Number(String(targetFilter.value).replace(/\D/g, '')), // remove non-numeric characters
          dateType: TargetLookbackPeriod.Days,
        };
      }
    }
    return {
      key,
      targetAudience: newTargetAudience,
      ids: {
        targetAudience: targetFilter.id,
        segmentId: segment.id,
        percentageSplitTestId: segment.percentageSplits[0]?.id,
        cycleId: segment.percentageSplits[0]?.cycles[0]?.id,
        purchaseCheckId: segment.segmentFilters[0]?.find(f => f.options.includes(SegmentFilterOptions.PurchaseCheck))
          ?.id,
      },
    };
  } else {
    const key = getStateKey(segment);
    return { key };
  }
}

function getPurchaseCheckFromSegment(
  segment: Segment<true>,
): {
  enablePurchaseCheck?: boolean;
  purchaseCheckDays?: number;
  purchaseCheckMonths?: number;
  purchaseCheckUnit?: PurchaseCheckUnit;
  purchaseCheckStartDate?: Moment;
  purchaseCheckEndDate?: Moment;
  isPurchaseCheckUsingDateRange?: boolean;
} {
  const bulkFilters = getBulkRetargetingFilters(segment);
  const purchaseFilter = bulkFilters.find(filter => filter.options.includes(SegmentFilterOptions.PurchaseCheck));
  if (purchaseFilter) {
    // Check if using date range (comparison === 'between')
    if (purchaseFilter.comparison === 'between') {
      const dates = String(purchaseFilter.value).split('|');
      // Parse UTC timestamps and convert to local time moments (preserving the calendar date)
      // Extract calendar date first to avoid timezone shift when converting end of day
      const purchaseCheckStartDateUTC = moment.utc(Number(dates[0]));
      const purchaseCheckEndDateUTC = moment.utc(Number(dates[1]));
      return {
        enablePurchaseCheck: true,
        purchaseCheckDays: 0, // Date range mode doesn't use days/months, same as target audience days: 0
        purchaseCheckMonths: 0, // Date range mode doesn't use days/months
        purchaseCheckUnit: PurchaseCheckUnit.Days,
        purchaseCheckStartDate: moment.tz(
          purchaseCheckStartDateUTC.format('YYYY-MM-DD') + 'T00:00:00',
          moment.tz.guess(),
        ),
        purchaseCheckEndDate: moment.tz(purchaseCheckEndDateUTC.format('YYYY-MM-DD') + 'T23:59:59', moment.tz.guess()),
        isPurchaseCheckUsingDateRange: true,
      };
    } else {
      // Days/months mode: parse the value and check for suffix
      const valueStr = String(purchaseFilter.value);
      const hasDaysSuffix = valueStr.endsWith('d');
      const hasMonthsSuffix = valueStr.endsWith('M');

      let purchaseCheckDays: number;
      let purchaseCheckMonths: number;
      let purchaseCheckUnit = PurchaseCheckUnit.Days;

      if (hasMonthsSuffix) {
        // Value ends with 'M' - it's months
        const parsedMonths = Number(valueStr.slice(0, -1));
        purchaseCheckMonths = isNaN(parsedMonths) ? 0 : parsedMonths;
        purchaseCheckUnit = PurchaseCheckUnit.Months;
        purchaseCheckDays = 0; // Not used in months mode, same as target audience days: 0
      } else if (hasDaysSuffix) {
        // Value ends with 'd' - it's days
        const parsedDays = Number(valueStr.slice(0, -1));
        purchaseCheckDays = isNaN(parsedDays) ? 0 : parsedDays;
        purchaseCheckUnit = PurchaseCheckUnit.Days;
        purchaseCheckMonths = 0; // Not used in days mode, same as target audience days: 0
      } else {
        // No suffix - assume it's days (backward compatibility with old format)
        const parsedDays = Number(valueStr);
        purchaseCheckDays = isNaN(parsedDays) ? 0 : parsedDays;
        purchaseCheckUnit = PurchaseCheckUnit.Days;
        purchaseCheckMonths = 0; // Not used in days mode, same as target audience days: 0
      }

      return {
        enablePurchaseCheck: true,
        purchaseCheckDays,
        purchaseCheckMonths,
        purchaseCheckUnit,
        purchaseCheckStartDate: undefined,
        purchaseCheckEndDate: undefined,
        isPurchaseCheckUsingDateRange: false,
      };
    }
  }
  return {};
}

function getAdditionalSegmentationFromSegment(
  segment: Segment<true>,
  t: TFunction,
): { additionalSegmentation?: SegmentationRule<any, any, any>[][] } {
  const bulkFilters = getBulkRetargetingFilters(segment);
  const isBrowseAbandonment = !!bulkFilters.find(filter =>
    filter.options.includes(SegmentFilterOptions.BrowseAbandonment),
  );
  const filterFactory = new FilterOptionsFactory(t, isBrowseAbandonment, true);
  const additionalSegmentation = segment.segmentFilters.map(x =>
    x
      .filter(f => f.type !== SegmentFilterType.BulkRetargeting)
      .reduceRight((acc, curr) => {
        const rule: SegmentationRule<any, any, any> = {
          configuration: {
            condition: filterFactory.inputTypeSelector(curr.options.join('.*.')) !== 'bool' ? curr.comparison : '',
            value: curr.value,
          },
          type: curr.options.join('.*.'),
        };
        return [rule, ...acc];
      }, [] as SegmentationRule<any, any, any>[]),
  );
  return { additionalSegmentation };
}

function getContentFromSegment(
  segment: Segment<true>,
  subjectLines: SubjectLine[],
  templates: Template[],
): { subjectLine?: { id: string; label: string }; template?: Template } {
  let subjectLineData: { id: string; label: string } | undefined = undefined;
  const subjectLineId = segment.percentageSplits[0]?.cycles[0]?.subjectLineId;
  if (subjectLineId && subjectLines?.length) {
    const foundSubjectLine = subjectLines.find(sl => sl.id === subjectLineId);
    if (foundSubjectLine) {
      subjectLineData = {
        id: foundSubjectLine.id,
        label: getFirstNonEmptyValue(foundSubjectLine.subjectLines, i18n.language),
      };
    }
  }
  let templateData: Template | undefined = undefined;
  const templateId = segment.percentageSplits[0]?.cycles[0]?.templateId;
  if (templateId && templates?.length) {
    const foundTemplate = templates.find(temp => temp.id === templateId);
    if (foundTemplate) {
      templateData = foundTemplate;
    }
  }
  return { subjectLine: subjectLineData, template: templateData };
}

function filterByTargetAudience(s: string): boolean {
  return (
    s === SegmentFilterOptions.BasketAbandonment ||
    s === SegmentFilterOptions.BrowseAbandonment ||
    s === SegmentFilterOptions.Purchase
  );
}

function getBulkRetargetingFilters(segment: Segment<true>): any[] {
  return segment?.segmentFilters.flatMap(x => x).filter(s => s.type === SegmentFilterType.BulkRetargeting) || [];
}

function getStateKey(segment: Segment<true>): TargetAudienceType | undefined {
  const keys = segment.segmentFilters
    .flatMap(x => x)
    .map(filter => filter.options.filter(filterByTargetAudience)[0] as TargetAudienceType)
    .filter(k => k);
  return keys[0];
}
